import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Instagram } from "lucide-react";
import { ekkoText } from "@/lib/ekkoDefaults";
import { buttonText } from "@/lib/buttonTexts";

export default function InstagramSection({ content = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const instagramHandle = ekkoText(content, "instagram_handle");
  const instagramUrl = ekkoText(content, "instagram_url");
  const instagramTitle = ekkoText(content, "instagram_title");
  const instagramSubtitle = ekkoText(content, "instagram_subtitle");

  // Fotos escolhidas no painel (só as que têm imagem)
  // As 8 mais recentes: duas linhas completas no computador
  const posts = (content.instagram_posts || []).filter((post) => post && post.image).slice(0, 8);
  // Só links http(s); qualquer outra coisa abre o perfil
  const postUrl = (post) => (/^https?:\/\//i.test(post.url || "") ? post.url : instagramUrl);

  return (
    <section
      id="instagram"
      ref={ref}
      className="py-24 md:py-32 bg-[#111111] relative"
      data-testid="instagram-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F5F5F5] mb-4 tracking-wider rich-text-content"
            data-testid="instagram-title"
            dangerouslySetInnerHTML={{ __html: instagramTitle }}
          />
          {instagramSubtitle && (
            <p 
              className="text-[#C9C9C9] text-base md:text-lg max-w-2xl mx-auto mb-4 rich-text-content"
              data-testid="instagram-subtitle"
              dangerouslySetInnerHTML={{ __html: instagramSubtitle }}
            />
          )}
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#C9C9C9] hover:text-[#A6C48A] transition-colors text-lg"
            data-testid="instagram-handle"
          >
            <Instagram size={24} />
            {instagramHandle}
          </a>
        </motion.div>

        {/* Fotos escolhidas no painel; sem fotos, a secção fica só com título e botão */}
        {posts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="instagram-feed"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {posts.map((post, index) => (
                <motion.a
                  key={`${post.image}-${index}`}
                  href={postUrl(post)}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: 0.05 * index }}
                  className="aspect-[4/5] overflow-hidden relative group bg-[#010000] rounded-2xl border border-[#2A2A2A]"
                  data-testid={`instagram-post-${index}`}
                >
                  <img
                    src={post.image}
                    alt={`Publicação ${index + 1} do Instagram ${instagramHandle}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-[#A6C48A]/0 group-hover:bg-[#010000]/30 transition-colors duration-300 flex items-center justify-center">
                    <Instagram
                      className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      size={40}
                    />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-10"
        >
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-skew inline-flex items-center gap-3 bg-[#A6C48A] text-[#010000] px-8 py-3 font-bold uppercase tracking-wider hover:bg-[#B9D3A1] transition-colors"
            data-testid="instagram-cta"
          >
            <Instagram size={18} className="transform skew-x-[10deg]" />
            <span>{buttonText(content, "instagram_cta")} {instagramHandle}</span>
          </a>
        </motion.div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-24" />
    </section>
  );
}
